import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useRealtimeUnits, useRealtimeTags } from 'Core/hooks';
// Actions
import {
  clearRedux,
  openDriverDeleteModal,
  openDriverTransaction,
  toggleDriversFilters,
  setDriversEntity,
  sendGenericExport,
  getCurrentSubUser,
  openResetPasswordDriverModal,
  savePreference,
  manageFiltersDrivers,
} from "Redux/actions";

import {
  openDriverCreateEditModalWithData,
  searchEntityLists,
} from "../createEditDriver/redux/slices";
import { removeDriverRegister, searchDrivers, searchSupervisor, setDriversSearchText, upsertDriverRegister } from "./redux/slices";

import { TagsGroup } from "Components/Tag";

import { setOpenChangeStatus } from "../changeStatus/redux/slices";

// Components
import { Grid, Grow, Typography, Box } from "@mui/material";
import { Avatar } from "Components";

import { Toolbar, Button } from "Components/uiControls";
import DriversAdvanceFilters from "../advanceFilters";
import CreateEditDriver from "../createEditDriver";
import DeleteDriver from "../deleteDriver";
import ImportDriver from "../importDriver";
import ResumeTransaction from "../resumeTransaction";
import TimeClock from "../timeClock";
import { HasPermission } from "Core/security";
import colors from 'Assets/sass/core/vars.scss';

import { openListModal as openTimeClockListModal } from "../timeClock/redux/slice";

import { NotificationManager } from "Components/Notifications";

import "./styles.scss";
import Preferences from "Core/containers/Preferences";
import { useSupervisorPermission } from "../../../../../.common/utils/supervisorPermission";
import ChangeStatus from "../changeStatus";
import ResetPasswordDriver from "../resetPasswordDriver";
import { completeColumns } from "Core/data/Helpers";
import { setImportNotificationDriver, openImportDriversModal } from "../importDriver/redux/slices";
import ImportResultsModal from "../../../../../../core/views/importResultsModal";

import { Tooltip } from "Components";

//Controls
import {
  Container,
  MenuComponent,
  SearchInput,
  TableButtons,
  TableView,
  useTablePagination,
} from "@gpstrackit/gtc-react-controls";

import Pill from "Components/Pill";
import { disableItemSelection, doesDriverMatchView, resolveDriverSupervisor } from "../../utils";
import { useImportNotifications } from "../../../../../../core/hooks";

const PATH_ICONS = `/assets/icons/`;
const MANAGE_DRIVERS_VIEW_PREFERENCE = "manageDriversView";
const INITIAL_SORT = { key: "title", order: "asc" };
const SEARCH_FIELDS = [
  "externalId",
  "title",
  "status",
  "supervisor_firstName",
  "supervisor_lastName",
];

const CSV_ONLY_KEYS = [
  "hosRulesetIds",
  "hosExempt",
  "hosExemptComment",
  "personalConveyance",
  "allowedPcDistance",
  "yardMoves",
  "employeeNumber",
];

const CSV_ONLY_KEYS_PDF = [
  "driverPin",
  "licenseNumber",
  "licenseCountry",
  "licenseState",
  "timeZone",
  "keyfob",
  "employeeNumber",
  "hosRulesetIds",
  "hosExempt",
  "hosExemptComment",
  "personalConveyance",
  "allowedPcDistance",
  "yardMoves",
];

const DriverTable = () => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();

  // State manager
  const loading = useSelector((state) => state.driverTableSliceRedux.loading);
  const filters = useSelector((state) => state.driverTableSliceRedux.filters);
  const driverRows = useSelector((state) => state.driverTableSliceRedux.registers);
  const openFilters = useSelector((state) => state.driverTableSliceRedux.openFilters);
  const selectedEntity = useSelector((state) => state.driverTableSliceRedux.entity);
  const error = useSelector((state) => state.driverTableSliceRedux.errorSearch);
  const searchText = useSelector((state) => state.driverTableSliceRedux.searchText);

  const openCreateEdit = useSelector((state) => state.driverCreateEditSliceRedux.openCreateEdit);
  const openDelete = useSelector((state) => state.driverDeleteDriverSliceRedux.openDelete);
  const openChangeStatus = useSelector((state) => state.driverChangeStatusSliceRedux.openChangeStatus);
  const openImport = useSelector((state) => state.driversImport.openImport);
  const openTransaction = useSelector((state) => state.driverRedux.openTransaction);
  const openTimeClock = useSelector((state) => state.driverTimeClockSliceRedux.open);

  // Use realtime hooks for catalogs
  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });

  const loadingTags = useSelector((state) => state.catalogsRedux.loadingTags);
  const timeZones = useSelector((state) => state.catalogsRedux.timeZones);
  const loadingTimeZones = useSelector((state) => state.catalogsRedux.loadingTimeZones);
  const migrationPushNotification = useSelector((state) => state.catalogsRedux.migrationPushNotification);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const loadingExport = useSelector((state) => state.commomRedux.loadingExport);
  const userSecurity = useSelector((state) => state.securityRedux.user);
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);

  const driverImport = useSelector((state) => state.driversImport);
  const { loadingCurrentSubUser, currentSubUser } = useSelector((state) => state.subUserRedux);
  const supervisorsCatalog = useSelector((state) => state.driverTableSliceRedux.supervisor);
  const missingSupervisor = useSelector((state) => state.driverTableSliceRedux.missingSupervisor);
  const entityHosRuleSetList = useSelector((state) => state.driverCreateEditSliceRedux.entityHosRuleSetList);

  const hosRuleSetMap = useMemo(() => {
    return (entityHosRuleSetList || []).reduce((acc, rule) => {
      acc[String(rule.entityId)] = rule.label || rule.value1 || "";
      return acc;
    }, {});
  }, [entityHosRuleSetList]);

  const openResetPasswordDriver = useSelector((state) => state.driverResetPasswordSliceRedux.openResetPasswordDriver);

  useImportNotifications(
    state => state.driversImport,
    setImportNotificationDriver
  );

  const [hasFilters, setHasFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferencesDrivers, setPreferencesDrivers] = useState({});
  const [loadPreferencesDrivers, setLoadPreferencesDrivers] = useState(true);
  const [loadColumnsDrivers, setLoadColumnsDrivers] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [currentSort, setCurrentSort] = useState(INITIAL_SORT);
  const [reloadKey, setReloadKey] = useState(0);

  const setSearchText = useCallback((text) => {
    dispatch(setDriversSearchText(text));
  }, [dispatch]);

  // searchText lives in the slice so socket updates can apply it; reset it when leaving the view
  useEffect(() => () => {
    dispatch(setDriversSearchText(null));
  }, [dispatch]);

  useEffect(() => {
    if (missingSupervisor) dispatch(searchSupervisor({}));
  }, [dispatch, missingSupervisor]);

  const resolveSupervisor = useCallback(
    (driver) => resolveDriverSupervisor(driver, supervisorsCatalog),
    [supervisorsCatalog]
  );


  // Zlogs permission (HOS / PC fields)
  const hasZlogsPermission = Boolean(HasPermission(userSecurity, "zlogs"));
  const isKph = Boolean(userSecurity?.isKph);
  const tableRef = useRef();
  const pagination = useTablePagination({
    dispatch,
    isLocalData: true,
    searchFields: SEARCH_FIELDS,
    tableRef: tableRef,
    sendGenericExport,
    exportName: "drivers",
  });

  const unitLookup = useMemo(() => {
    return new Map((units || []).map((unit) => [String(unit.id), unit]));
  }, [units]);

  const filteredData = useMemo(() => {
    const normalizedDrivers = (driverRows || []).map((driver) => {
      const driverId = driver?.driverId || driver?.id;
      const unitId = driver?.unit?.id || driver?.unitId || null;
      const unitFromCatalog = unitId ? unitLookup.get(String(unitId)) : null;

      return {
        ...driver,
        id: String(driverId),
        driverId,
        unitId: unitId ? String(unitId) : null,
        unit: unitId ? {
          ...(driver?.unit || {}),
          id: String(unitId),
          label: unitFromCatalog?.label || driver?.unit?.label || "",
          icon: unitFromCatalog?.icon || driver?.unit?.icon || null,
        } : null,
        fsmUsername: driver?.fsmDriver?.username || driver?.fsmUsername || "",
      };
    });

    const sortedDrivers = [...normalizedDrivers].sort((a, b) => {
      let aValue, bValue;
      switch (currentSort.key) {
        case 'name':
        case 'title':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'externalId':
          aValue = a.externalId || '';
          bValue = b.externalId || '';
          break;
        default:
          return 0;
      }
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      if (currentSort.order === 'asc') {
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      } else {
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      }
    });

    return sortedDrivers;
  }, [driverRows, unitLookup, currentSort]);

  const drivers = useMemo(() => {
    const currentPage = pagination.defaultPage || 1;
    const rowsPerPage = pagination.rowsPerPage || filteredData.length;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, pagination.defaultPage, pagination.rowsPerPage]);

  const doesDriverMatchCurrentView = useCallback(
    (driver) => doesDriverMatchView(driver, { filters, searchText, supervisors: supervisorsCatalog }),
    [filters, searchText, supervisorsCatalog]
  );

  const handleDriverSaved = useCallback((driver) => {
    if (!driver) return;

    if (doesDriverMatchCurrentView(driver)) {
      dispatch(upsertDriverRegister(driver));
      return;
    }

    dispatch(removeDriverRegister(driver?.driverId || driver?.id));
  }, [dispatch, doesDriverMatchCurrentView]);

  useEffect(() => {
    if (loadPreferencesDrivers) return;

    const timeoutId = setTimeout(() => {
      const conditions = [...(currentFilters || [])];
      if (searchText?.trim()) {
        conditions.push({ fields: SEARCH_FIELDS, terms: searchText.trim() });
      }

      dispatch(
        searchDrivers({
          conditions,
          limit: 5000,
          offset: 0,
        })
      );
    }, searchText ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [dispatch, loadPreferencesDrivers, currentFilters, searchText, reloadKey]);

  useEffect(() => {
    tableRef?.current?.setPage(1);
  }, [searchText]);

  const isSupervisor = useSupervisorPermission(userSecurity, currentSubUser);
  // Permissions
  const editablePermissions = HasPermission(userSecurity, "", [342]);
  const deleteDriverPermission = HasPermission(userSecurity, ["delDriver"], [319]);
  const hasELDPermission = HasPermission(userSecurity, "zlogs");
  const isLoading = loading || loadingTimeZones || loadingTags || loadingExport;
  const optionsMenu = [];

  if (editablePermissions) {
    optionsMenu.push(messages["settingsDriver_resetPassword"]);
    optionsMenu.push(messages["edit"]);
  }
  if (deleteDriverPermission) {
    optionsMenu.push(messages["delete"]);
  }

  if (exportResult && exportName === "drivers") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      7000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(clearRedux("SETTINGS_DRIVER", { errorSearch: false }));
  }

  useEffect(() => {
    if (userSecurity?.isSubUser && userSecurity?.subUserId) {
      dispatch(getCurrentSubUser({ id: userSecurity?.subUserId }));
    }
  }, [userSecurity?.isSubUser, userSecurity?.subUserId, dispatch]);

  useEffect(() => {
    if (
      hasZlogsPermission &&
      (!entityHosRuleSetList || entityHosRuleSetList.length === 0)
    ) {
      dispatch(
        searchEntityLists({
          terms: ["hosRuleSet"],
          entityName: "entityName",
        })
      );
    }
  }, [hasZlogsPermission, entityHosRuleSetList, dispatch]);

  const entities = [
    {
      name: messages["settingsDriver_drivers"],
      id: "drivers",
    },
    {
      name: messages["settingsDriver_tags"],
      id: "tags",
    },
  ];

  if (HasPermission(userSecurity, ["fcscoll"])) {
    entities.push({
      name: messages["settingsDriver_driversFaces"],
      id: "driverFaces",
    });
  }

  //Define the columns we're going to use in the table
  useEffect(() => {
    if (loadPreferencesDrivers) return;

    const baseColumns = [
      {
        key: "id",
        title: messages["settingsDriver_id"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.driverId",
      },
      {
        key: "avatar",
        title: messages["settingsDriver_avatar"],
        show: true,
        export: false,
        render: (row) => {
          return (
            <Grid container w="50px">
              <Avatar fileKey={row?.avatarFile?.fileKey} name={row?.name} />
            </Grid>
          );
        },
      },
      {
        key: "externalId",
        title: messages["settingsDriver_externalId"],
        show: true,
        sort: true,
        sortField: "externalId",
        export: true,
        render: (row) => {
          return (
            <Grid
              container
              alignItems="center"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item zeroMinWidth style={{ maxWidth: 120 }}>
                <Typography noWrap>{row?.externalId || ""}</Typography>
              </Grid>
            </Grid>
          );
        },
        exportValue: (row) => {
          return row?.externalId || "";
        },
        sortValue: (row) => {
          return row?.externalId || "";
        },
        functionString: "return row?.externalId",
      },
      {
        key: "name",
        title: messages["settingsDriver_driver"],
        show: true,
        sort: true,
        disabled: true,
        sortField: "title",
        export: true,
        render: (row) => {
          return (
            <Grid
              container
              alignItems="center"
              justifyContent="flex-start"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item zeroMinWidth style={{ width: 150 }}>
                <Typography noWrap>
                  {row?.name?.length > 15 ? (
                    <Tooltip title={row?.name}>
                      <Typography noWrap>
                        {row?.name.substring(0, 15) + "..."}
                      </Typography>
                    </Tooltip>
                  ) : (
                    row?.name
                  )}
                </Typography>
              </Grid>
              <Grid item>
                {(editablePermissions || deleteDriverPermission) &&
                  <MenuComponent
                    options={optionsMenu}
                    icon={"more_vert"}
                    className="context-menu"
                    onClick={(type, value) => {
                      if (type === messages["edit"]) {
                        dispatch(
                          openDriverCreateEditModalWithData({
                            open: true,
                            driverGroup: value,
                          })
                        );
                      } else if (type === messages["delete"]) {
                        dispatch(
                          openDriverDeleteModal({
                            open: true,
                            items: [value],
                          })
                        );
                      } else if (
                        type === messages["settingsDriver_resetPassword"]
                      ) {
                        dispatch(
                          openResetPasswordDriverModal({
                            open: true,
                            driver: value,
                          })
                        );
                      }
                    }}
                    value={row}
                  />
                }
              </Grid>
            </Grid>
          );
        },
        functionString: "return row?.name?.slice(0,35)",
      },
      {
        key: "supervisor",
        title: messages["settingsDriver_supervisor"],
        show: isSupervisor,
        export: isSupervisor,
        render: (row) => {
          const supervisor = resolveSupervisor(row);
          const fullname = `${supervisor?.firstName || ""} ${supervisor?.lastName || ""}`;
          return (
            <Grid
              container
              alignItems="center"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item zeroMinWidth style={{ maxWidth: 120 }}>
                <Typography noWrap>
                  {fullname?.trim()?.length > 15 ? (
                    <Tooltip title={fullname.trim()}>
                      <Typography noWrap>
                        {fullname.trim().substring(0, 15) + "..."}
                      </Typography>
                    </Tooltip>
                  ) : (
                    fullname || ""
                  )}
                </Typography>
              </Grid>
            </Grid>
          );
        },
        exportValue: (row) => {
          const supervisor = resolveSupervisor(row);
          return supervisor?.firstName || "";
        },
        sortValue: (row) => {
          const supervisor = resolveSupervisor(row);
          return supervisor?.firstName || "";
        },
        functionString: `
            const firstName = row?.supervisor?.firstName ?? ''; 
            const lastName = row?.supervisor?.lastName ?? '';
            return firstName + ' ' + lastName
          `,
      },
      {
        key: "vehicle",
        title: messages["settingsDriver_vehicle"],
        show: true,
        export: true,
        render: (row) => {
          const unitId = row?.unit;
          if (!unitId) return "";
          return (
            <Grid
              container
              alignItems="center"
              className="context-menu-container"
              wrap="nowrap"
            >
              {row?.unit?.icon && (
                <img
                  style={{ marginRight: "10px" }}
                  src={`${PATH_ICONS}${row?.unit?.icon}`}
                  alt=""
                />
              )}
              <Grid item zeroMinWidth style={{ width: 300 }}>
                <Typography noWrap>
                  {unitId?.label?.length > 30 ? (
                    <Tooltip title={unitId?.label}>
                      <Typography noWrap>
                        {unitId?.label.substring(0, 30) + "..."}
                      </Typography>
                    </Tooltip>
                  ) : (
                    unitId?.label
                  )}
                </Typography>
              </Grid>
            </Grid>
          );
        },
        exportValue: (row) => {
          let unit = units.find((u) => u?.id == row?.unitId);
          if (!unit) return "";
          return unit?.label;
        },
        sortValue: (row) => {
          let unit = units.find((u) => u?.id == row?.unitId);
          if (!unit) return "";
          return unit?.label;
        },
        functionString: "return row?.unit?.label",
      },
      {
        key: "status",
        title: messages["settingsDriver_status"],
        show: true,
        export: true,
        render: (row) => {
          return (
            <React.Fragment>
              <div style={{ marginBottom: 5 }}>
                <Pill
                  colorField="color"
                  textField="label"
                  style={{ border: "0", width: "100px" }}
                  item={{
                    label: row.status
                      ? messages["active"]
                      : messages["inactive"],
                    color: row.status ? "#66bb6a" : "#bdbdbd",
                  }}
                />
              </div>
            </React.Fragment>
          );
        },
        exportValue: (row) => {
          return row.status
            ? messages["active"]
            : messages["inactive"];
        },
        sortValue: (row) => {
          return row.status;
        },
        functionString: `return row?.status ? '${messages["active"]}' : '${messages["inactive"]}'`,
      },
      {
        key: "timezone",
        title: messages["settingsDriver_timezone"],
        show: true,
        export: true,
        render: (row) => {
          let timeZone = timeZones.find((tz) => tz.id == row.idTimeZone);
          return timeZone?.name || "";
        },
        exportValue: (row) => {
          let timeZone = timeZones.find((tz) => tz.id == row.idTimeZone);
          return timeZone?.name || "";
        },
        sortValue: (row) => {
          let timeZone = timeZones.find((tz) => tz.id == row.idTimeZone);
          return timeZone?.name || "";
        },
        functionString: "return row?.timeZone?.name",
      },
      {
        key: "tagIds",
        title: messages["settingsDriver_tags"],
        show: true,
        export: true,
        render: (row) => {
          let tagIds = row?.tagIds || [];
          let currentTags = [];
          let tagIdsArray;
          if (Array.isArray(tagIds)) {
            tagIdsArray = tagIds;
          } else if (typeof tagIds === 'string') {
            try {
              tagIdsArray = JSON.parse(tagIds);
            } catch (e) {
              tagIdsArray = [];
            }
          } else {
            tagIdsArray = [];
          }
          if (tagIdsArray.includes(-99)) {
            currentTags = [{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: colors?.blue_gtc }];
          } else {
            tags?.forEach((tag) => {
              if (tagIdsArray?.includes(tag.id)) {
                currentTags.push(tag);
              }
            });
          }
          return (
            <div style={{ width: 200, maxWidth: 200 }}>
              <TagsGroup
                options={currentTags}
                nameField="label"
                colorField="color"
              />
            </div>
          );
        },
        exportValue: (row) => {
          let tagIds = row?.tagIds || [];
          let tagIdsArray;
          if (Array.isArray(tagIds)) {
            tagIdsArray = tagIds;
          } else if (typeof tagIds === 'string') {
            try {
              tagIdsArray = JSON.parse(tagIds);
            } catch (e) {
              tagIdsArray = [];
            }
          } else {
            tagIdsArray = [];
          }
          if (tagIdsArray.includes(-99)) {
            return messages['tagPicker_allTagsAreSelected'];
          }
          const tagNames = tags?.filter(tag => tagIdsArray?.includes(tag.id)).map(tag => tag.label);
          return tagNames && tagNames.length ? tagNames.join(' / ') : '';
        },
        sortValue: (row) => {
          if (!row?.tagIds) return "";
          let tagIds = row?.tagIds;
          let tagIdsArray;
          if (Array.isArray(tagIds)) {
            tagIdsArray = tagIds;
          } else if (typeof tagIds === 'string') {
            try {
              tagIdsArray = JSON.parse(tagIds);
            } catch (e) {
              tagIdsArray = [];
            }
          } else {
            tagIdsArray = [];
          }
          const driverTags = tags?.filter(tag => tagIdsArray?.includes(tag.id));
          if (!driverTags?.length) return "";
          return driverTags.map(tag => tag.label).join(' - ');
        },
        functionString: `return row?.tags?.length ? row?.tags?.map((item, index) => item?.label).join(" / ") : '${messages["infoBubble_allTags"]}'`,
      },
      {
        key: "keyfob",
        title: messages["settingsDriver_keyFobHex"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.keyfob?.hex",
        exportValue: (row) => {
          return row?.keyfob?.hex || "";
        },
      },
      {
        key: "email",
        title: messages["settingsDriver_email"],
        show: true,
        export: true,
        render: (row) => {
          return row.email;
        },
        exportValue: (row) => {
          return row.email;
        },
        functionString: `return row?.email`,
      },
      {
        key: "phone",
        title: messages["settingsDriver_phone"],
        show: true,
        export: true,
        render: (row) => {
          return row.phone;
        },
        exportValue: (row) => {
          return row.phone;
        },
        functionString: `return row?.phone`,
      },
      {
        key: "username",
        title: messages["settingsDriver_fsmLogin"],
        show: true,
        export: true,
        render: (row) => {
          return row?.fsmUsername
            ? row?.fsmUsername
            : "";
        },
        exportValue: (row) => {
          return row?.fsmUsername
            ? row?.fsmUsername
            : "";
        },
        functionString: `return row?.fsmDriver.username ? row?.fsmDriver.username : ''`,
      },
      // Export-only columns for creation/edition
      {
        key: "driverPin",
        title: messages["settingsDriver_driverPin"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.driverPin",
        exportValue: (row) => {
          return row?.driverPin || "";
        },
      },
      {
        key: "licenseNumber",
        title: messages["settingsDriver_licenseNumber"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.licenseNumber",
      },
      {
        key: "licenseCountry",
        title: messages["settingsDriver_licenseCountry"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.licenseCountry",
      },
      {
        key: "licenseState",
        title: messages["settingsDriver_licenseState"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.licenseState",
      },
      {
        key: "timeZone",
        title: messages["settingsDriver_timezone"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.timeZone?.title",
      },
    ];

    // Columns related to HOS / Zlogs (only for users with Zlogs permission)
    const hosZlogsColumns = [
      {
        key: "employeeNumber",
        title: messages["settingsDriver_employeeNumber"],
        show: false,
        export: true,
        exportOnly: true,
        functionString: "return row?.empNum",
        exportValue: (row) => {
          return row?.empNum || "";
        },
      },
      {
        key: "hosRulesetIds",
        title: messages["settingsDriver_hosRuleSet"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => {
          const raw = row?.hosRulesetIds;
          if (raw == null) return "";

          let ids = [];

          if (Array.isArray(raw)) {
            ids = raw;
          } else if (typeof raw === "string") {
            try {
              const parsed = JSON.parse(raw);
              ids = Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              ids = raw.includes(",") ? raw.split(",") : [raw];
            }
          } else {
            ids = [raw];
          }

          const rulesetId = ids?.[0];
          if (rulesetId == null) return "";

          return hosRuleSetMap[String(rulesetId)] || String(rulesetId);
        },
        functionString: `
          const map = ${JSON.stringify(hosRuleSetMap)};
          const raw = row?.hosRulesetIds;
          if (raw == null) return '';

          let ids = [];

          if (Array.isArray(raw)) {
            ids = raw;
          } else if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              ids = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              ids = raw.includes(',') ? raw.split(',') : [raw];
            }
          } else {
            ids = [raw];
          }

          const rulesetId = ids?.[0];
          return map[String(rulesetId)] || String(rulesetId ?? '');
        `,
      },
      {
        key: "hosExempt",
        title: messages["settingsDriver_hosExempt"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => (row?.hosExempt ? "Yes" : "No"),
        functionString: `
          return row?.hosExempt ? 'Yes' : 'No';
        `,
      },
      {
        key: "hosExemptComment",
        title: messages["settingsDriver_hosExemptComment"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => {
          return row?.hosExemptComment || "";
        },
        functionString: `
          return row?.hosExemptComment || '';
        `,
      },
      {
        key: "personalConveyance",
        title: messages["settingsDriver_personalConveyance"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => {
          if (!row?.specialDutyStatuses) return "";
          try {
            const special =
              typeof row.specialDutyStatuses === "string"
                ? JSON.parse(row.specialDutyStatuses)
                : row.specialDutyStatuses;
            return special && special.PC ? "Yes" : "No";
          } catch {
            return "";
          }
        },
        functionString: `
          if (!row?.specialDutyStatuses) return '';
          try {
            const special = typeof row.specialDutyStatuses === 'string'
              ? JSON.parse(row.specialDutyStatuses)
              : row.specialDutyStatuses;
            return special && special.PC ? 'Yes' : 'No';
          } catch (e) {
            return '';
          }
        `,
      },
      {
        key: "allowedPcDistance",
        title: messages["settingsDriver_allowedPCDistance"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => {
          if (!row?.specialDutyStatuses) return "";
          try {
            const special =
              typeof row.specialDutyStatuses === "string"
                ? JSON.parse(row.specialDutyStatuses)
                : row.specialDutyStatuses;
            if (!special || special.PCD == null) return "";
            const miles = Number(special.PCD);
            if (!Number.isFinite(miles)) return "";
            if (isKph) {
              const km = Math.round(miles * 1.60934 * 100) / 100;
              return String(km);
            }
            return String(miles);
          } catch {
            return "";
          }
        },
        functionString: `
          if (!row?.specialDutyStatuses) return '';
          try {
            const special = typeof row.specialDutyStatuses === 'string'
              ? JSON.parse(row.specialDutyStatuses)
              : row.specialDutyStatuses;
            if (!special || special.PCD == null) return '';
            const miles = Number(special.PCD);
            if (!Number.isFinite(miles)) return '';
            const isKph = ${"Boolean("}(${isKph ? "true" : "false"})${")"};
            if (isKph) {
              const km = Math.round(miles * 1.60934 * 100) / 100;
              return String(km);
            }
            return String(miles);
          } catch (e) {
            return '';
          }
        `,
      },
      {
        key: "yardMoves",
        title: messages["settingsDriver_personalYardMoves"],
        show: false,
        export: true,
        exportOnly: true,
        exportValue: (row) => {
          if (!row?.specialDutyStatuses) return "";
          try {
            const special =
              typeof row.specialDutyStatuses === "string"
                ? JSON.parse(row.specialDutyStatuses)
                : row.specialDutyStatuses;
            return special && special.YM ? "Yes" : "No";
          } catch {
            return "";
          }
        },
        functionString: `
          if (!row?.specialDutyStatuses) return '';
          try {
            const special = typeof row.specialDutyStatuses === 'string'
              ? JSON.parse(row.specialDutyStatuses)
              : row.specialDutyStatuses;
            return special && special.YM ? 'Yes' : 'No';
          } catch (e) {
            return '';
          }
        `,
      },
    ];

    pagination.setColumns([
      ...baseColumns,
      ...(hasZlogsPermission ? hosZlogsColumns : []),
    ]);
  }, [locale, tags, timeZones, loadPreferencesDrivers, hasZlogsPermission, entityHosRuleSetList, hosRuleSetMap, resolveSupervisor]);

  const savePreferencesDrivers = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected preferences to be an array");
        return;
      }
      let updatedPreferences = { ...preferencesDrivers } || {};
      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });
      setPreferencesDrivers(updatedPreferences);
      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: MANAGE_DRIVERS_VIEW_PREFERENCE,
          preferenceValue: updatedPreferences,
        })
      );
    },
    [preferencesDrivers, authenticatedId, dispatch]
  );

  const refreshDriverTable = (filters) => {
    setSearchText(null);
    setHasFilters(false);
    setSelectedItems([]);
    tableRef?.current?.setSelectedItems?.([]);
    tableRef?.current?.setPage(1);
    // Force the search effect to re-fetch even when filters/search didn't change (e.g. after a delete)
    setReloadKey((key) => key + 1);
    if (filters) {
      const { includeInactiveDrivers, unassigned, tagIds, unitIds, entity } = filters;
      const conditions = [];
      if (!includeInactiveDrivers) {
        setHasFilters(true);
        conditions.push({ fields: ["status"], terms: 1 });
      }
      if (!unassigned) {
        setHasFilters(true);
        conditions.push({ fields: ["unit_label"], terms: "" });
      }
      if (tagIds?.length) {
        setHasFilters(true);
        conditions.push({ fields: ["tagIds"], terms: tagIds });
      }
      if (unitIds?.length) {
        setHasFilters(true);
        conditions.push({ fields: ["unit_id"], terms: unitIds });
      }
      setSelectedItems([]);
      setSearchText(null);
      setCurrentFilters(conditions);
      dispatch(manageFiltersDrivers(filters));
      tableRef?.current?.setPage(1);
      setPreferencesDrivers(filters);
    }
    if (preferencesDrivers) {
      const { includeInactiveDrivers, unassigned, tagIds, unitIds } = preferencesDrivers;
      const conditions = [];
      if (!includeInactiveDrivers) {
        setHasFilters(true);
        conditions.push({ fields: ["status"], terms: 1 });
      }
      if (!unassigned) {
        setHasFilters(true);
        conditions.push({ fields: ["unit_label"], terms: "" });
      }
      if (tagIds?.length) {
        setHasFilters(true);
        conditions.push({ fields: ["tagIds"], terms: tagIds });
      }
      if (unitIds?.length) {
        setHasFilters(true);
        conditions.push({ fields: ["unit_id"], terms: unitIds });
      }
      setSelectedItems([]);
      setSearchText(null);
      tableRef?.current?.setPage(1);
    }
  };

  useEffect(() => {
    if (loadPreferencesDrivers) return;
    if (preferencesDrivers?.columns?.length && pagination?.columns?.length) {
      const preferenceMap = new Map(
        (preferencesDrivers.columns || []).map((col) => [col.key, col])
      );
      const reorderedColumns = (preferencesDrivers.columns || [])
        .map((pref) => {
          const col = pagination.columns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);
      const newColumns = pagination.columns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));
      const finalColumns = [...reorderedColumns, ...newColumns];
      pagination.setColumns(finalColumns);
      setPreferencesDrivers({ ...preferencesDrivers });
      setLoadColumnsDrivers(true);
    }
  }, [preferencesDrivers?.columns, pagination?.columns?.length, loadPreferencesDrivers]);

  useEffect(() => {
    if (migrationPushNotification?.transactionId) {
      NotificationManager.success(
        messages["settingsDriver_importation_title"],
        messages["settingsDriver_importation_body"],
        4000,
        () => dispatch(openDriverTransaction(true)),
        null,
        "success",
        null
      );
    }
  }, [migrationPushNotification, messages, dispatch]);

  return (
    <>
      <Container maxWidth={false}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item md={4} className={"toolbar-by-drivers"}>
            <Toolbar className={"toolbar-by-drivers"}>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid
                  item
                  style={{
                    marginLeft: 6,
                  }}
                >
                  <Button
                    type="icon"
                    tooltip={messages["filters"]}
                    placement="bottom"
                    icon="filter_list"
                    disabled={loading || loadingTags || loadingTimeZones}
                    onClick={() => dispatch(toggleDriversFilters(!openFilters))}
                    className={
                      hasFilters
                        ? "select-view-icon-filter"
                        : "select-view-icon"
                    }
                  />
                </Grid>
                {editablePermissions &&
                  <Grid item>
                    <div style={{ marginLeft: 6 }}>
                      <Button
                        type="icon"
                        icon="add_box"
                        placement="bottom"
                        disabled={isLoading}
                        tooltip={messages["add"]}
                        onClick={() => {
                          dispatch(
                            openDriverCreateEditModalWithData({
                              open: true,
                              driverGroup: null,
                            })
                          );
                        }}
                      />
                    </div>
                  </Grid>
                }
                {deleteDriverPermission && (
                  <Grid item>
                    {selectedItems?.length > 0 && (
                      <Button
                        type="icon"
                        icon="delete"
                        placement="bottom"
                        tooltip={messages["delete"]}
                        disabled={!selectedItems?.length}
                        onClick={() => {
                          dispatch(
                            openDriverDeleteModal({
                              open: true,
                              items: selectedItems || [],
                            })
                          );
                        }}
                      />
                    )}
                  </Grid>
                )}
                <Grid item>
                  {editablePermissions && selectedItems?.length > 0 && (
                    <Button
                      type="icon"
                      icon="check_circle_outline"
                      placement="bottom"
                      tooltip={messages["settingsDriver_changeStatus"]}
                      disabled={!selectedItems?.length}
                      onClick={() => {
                        dispatch(
                          setOpenChangeStatus({
                            open: true,
                            items: selectedItems || [],
                          })
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid item>
                  <TableButtons
                    messages={messages}
                    tableRef={tableRef}
                    {...pagination}
                    columns={pagination.columns?.filter(col => col.exportOnly !== true)}
                    buttons={["export", ["orderColumn"]]}
                    disabled={isLoading}
                    onExport={(type) => {
                      const columns = pagination.columns || [];
                      let headers = columns.filter(c => (c.export || c.exportOnly) && c.key !== "id");
                      if (!hasZlogsPermission) {
                        headers = headers.filter(h => !CSV_ONLY_KEYS.includes(h.key));
                      }
                      if (type === "Pdf") {
                        headers = headers.filter(h => !CSV_ONLY_KEYS_PDF.includes(h.key));
                      }
                      if (type === "Csv") {
                        headers = [
                          {
                            key: "id",
                            title: messages["settingsDriver_id"] || "ID",
                            export: true,
                            exportOnly: true,
                            functionString: "return row?.driverId",
                          },
                          ...headers,
                        ];
                      }
                      if (selectedItems?.length) {
                        if (type === "Csv") {
                          const csvRows = [];
                          csvRows.push(headers.map(h => `"${(h.title || h.key)}"`).join(","));
                          selectedItems.forEach(item => {
                            const row = headers.map(h => {
                              let value;
                              if (h.key === "id") value = item.driverId ?? item.id;
                              else if (h.exportValue) value = h.exportValue(item);
                              else value = item[h.key];
                              if (typeof value === "object" && value !== null) value = JSON.stringify(value);
                              return `"${value !== undefined ? String(value).replace(/"/g, '""') : ""}"`;
                            });
                            csvRows.push(row.join(","));
                          });
                          const csvContent = csvRows.join("\n");
                          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url;
                          link.setAttribute("download", "settings-drivers.csv");
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                          return;
                        }
                        if (type === "Pdf") {
                          const exportOnlyKeys = columns.filter(c => c.exportOnly).map(c => c.key);
                          tableRef.current.exportPDF({
                            excludeColumns: ["id", ...exportOnlyKeys, ...CSV_ONLY_KEYS_PDF],
                          });
                          return;
                        }
                      }
                      const exportHeaders = headers.map(h => ({
                        header: h.title,
                        column: h.key,
                        functionString: h.functionString,
                      }));
                      let conditions = [...(currentFilters || [])];
                      if (searchText) {
                        conditions.push({ fields: SEARCH_FIELDS, terms: searchText });
                      }
                      dispatch(
                        sendGenericExport(
                          "drivers",
                          type,
                          `
                              query searchDrivers ($conditions: [DriverSearchInput], $sort: [DriverSortInput], $limit: Int, $offset: Int) {
                              searchDrivers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                                  items {
                                      driverId
                                      externalId
                                      name
                                      tags {
                                        id
                                        label
                                      }
                                      idTimeZone
                                      timeZone {
                                          id
                                          title
                                          name
                                      }
                                      empNum
                                      tagIds
                                      status
                                      licenseNumber
                                      licenseCountry
                                      licenseState
                                      driverPin
                                      hosRulesetIds
                                      hosExempt
                                      hosExemptComment
                                      specialDutyStatuses
                                      email
                                      phone
                                      notifyByEmail
                                      notifyBySms
                                      supervisorId
                                      supervisor {
                                          firstName
                                          lastName
                                      }
                                      fsmDriver {
                                          username
                                      }
                                      keyfob {
                                          hex
                                      }
                                      avatarFileId
                                      tempAvatarUrl
                                      avatarFile {
                                          id
                                          repositoryId
                                          repositoryType
                                          repositoryCode
                                          repositoryPath
                                          fileKey
                                          originalName
                                      }
                                      unit {
                                        id
                                        label
                                      }
                                  }
                                  total
                              }
                          }
                          `,
                          {
                            conditions,
                            sort: { field: "title", order: "ASC" },
                            limit: 10,
                            offset: 0,
                          },
                          exportHeaders,
                          {
                            unit: "pt",
                            size: "A4",
                            orientation: "landscape",
                            fontSize: 10,
                          },
                          null,
                          false,
                          true
                        )
                      );
                    }}
                    onChangeColumns={(columns) => {
                      savePreferencesDrivers([
                        { key: "columns", value: columns },
                      ]);
                    }}
                  />
                </Grid>
                {editablePermissions &&
                  <Grid item>
                    <Button
                      type="icon"
                      icon="ios_share"
                      tooltip={messages["settingsDriver_import_driver"]}
                      onClick={() => {
                        dispatch(
                          openImportDriversModal(true)
                        );
                      }}
                    />
                  </Grid>
                }
                <Grid item>
                  <div style={{ marginLeft: 6 }}>
                    {HasPermission(userSecurity, ["dapptck"], [247]) && (
                      <Button
                        type="icon"
                        icon="access_time_filled"
                        placement="bottom"
                        tooltip={messages["settingsDriver_timeCard"]}
                        onClick={() => {
                          dispatch(openTimeClockListModal());
                        }}
                      />
                    )}
                  </div>
                </Grid>
              </Grid>
            </Toolbar>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            style={{ textAlign: "center", marginBottom: 8 }}
          >
            {(entities || []).map((entity, index) => {
              return (
                <Pill
                  key={entity?.id || index}
                  textField={"name"}
                  item={entity}
                  outlined={selectedEntity?.id != entity?.id}
                  onClick={() => {
                    dispatch(setDriversEntity(entity));
                  }}
                  style={{ border: "0" }}
                  label="pill-label-text"
                  colorField="default"
                  colorSelection="005bf8"
                />
              );
            })}
          </Grid>
          <Grid item xs={12} sm={12} md={4} className="mb-10 text-right">
            <SearchInput
              searchText={searchText}
              onSearchText={(text) => setSearchText(text)}
              placeholder={messages["settingsDriver_search"]}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={MANAGE_DRIVERS_VIEW_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferencesDrivers) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT;
              setCurrentSort(sort);
              pagination.onChangeSort(sort);
              setPreferencesDrivers(data);
              setLoadPreferencesDrivers(false);
            }, 100);
          }}
        >
          <TableView
            {...pagination}
            keyColumn="id"
            ref={tableRef}
            columns={pagination.columns?.filter(col => col.exportOnly !== true)}
            total={filteredData?.length || 0}
            disableItemSelection={disableItemSelection(preferencesDrivers)}
            selectedItems={selectedItems}
            onSelectedItems={(items) => setSelectedItems(items)}
            loading={loading}
            topheight={235}
            messageNoData={messages["noMatchesFound"]}
            data={drivers || []}
            exportOrientation="landscape"
            exportName="settings-drivers"
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferencesDrivers([
                { key: "columns", value: finalsColumns },
              ]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              setCurrentSort(newSort);
              savePreferencesDrivers([{ key: "sort", value: newSort }]);
            }}
          />
        </Preferences>
        <DriversAdvanceFilters callback={refreshDriverTable} />
        <ImportResultsModal />
        {openCreateEdit && <CreateEditDriver onDriverSaved={handleDriverSaved} />}
        {openDelete && <DeleteDriver callback={refreshDriverTable} />}
        {openChangeStatus && (
          <ChangeStatus callback={refreshDriverTable} pagination={pagination} />
        )}
        {openImport && <ImportDriver callback={refreshDriverTable} />}
        {openTransaction && (
          <ResumeTransaction
            callback={refreshDriverTable}
            idTransaction={migrationPushNotification?.transactionId}
          />
        )}
        {openTimeClock && <TimeClock />}
        {openResetPasswordDriver && (
          <ResetPasswordDriver callback={refreshDriverTable} />
        )}
      </Container>
    </>
  );
};

export default DriverTable;